import { useState, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import CameraCapture from '../components/CameraCapture';
import DateRangePicker from '../components/DateRangePicker';
import { calcRental } from '../utils/calcRental';
import { countries } from '../data/countries';
import ContractDocument from '../components/ContractDocument';
import { buildContractContext } from '../utils/contractContext';
import { renderContract } from '../utils/renderContract';

const TIMES = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'];

function isAvailable(vid, d1str, d2str, rentals, bufferHours = 0) {
  const d1 = new Date(d1str), d2 = new Date(d2str);
  const bufferMs = (bufferHours || 0) * 3600000;
  return !rentals.some(r => {
    if (r.vid !== vid) return false;
    if (r.status === 'complete' || r.status === 'cancelled') return false;
    const rs = new Date(r.start), re = new Date(r.end);
    // Extend existing rental window by buffer on both sides
    const bufferedStart = new Date(rs.getTime() - bufferMs);
    const bufferedEnd   = new Date(re.getTime() + bufferMs);
    return d1 < bufferedEnd && d2 > bufferedStart;
  });
}

const STEPS = [
  { n: 1, label: 'Dates & Location' },
  { n: 2, label: 'Choose Vehicle' },
  { n: 3, label: 'Extras' },
  { n: 4, label: 'Your Details' },
  { n: 5, label: 'Payment' },
  { n: 6, label: 'Confirmed' },
];

const today = new Date().toISOString().slice(0, 10);
const defaultReturn = new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10);

function initBk() {
  return {
    step: 1,
    d1: today, d2: defaultReturn,
    t1: '10:00', t2: '10:00',
    days: 5,
    pickupLocId: null, returnLocId: null,
    diffReturn: false, customPickup: '',
    filterType: 'all',
    vid: null, vehName: '', vehIcon: '🚗', rate: 0,
    basePrice: 0, extrasPrice: 0, totalPrice: 0,
    selectedExtras: {},      // { extraId: qty } — 0 means not selected
    selectedInsurance: null, // insurance id
    promoCode: '',
    payMethod: 'cash',
    fname: '', lname: '', phone: '', email: '',
    passport: '', licence: '', licExpiry: '', notes: '',
    country: '', countryName: '', countryFlag: '',
    termsAgreed: false,
    licenceFile: null, licencePreview: null,
    idFile: null,      idPreview: null,
    presignOpen: false, presigned: false, presignData: null, presignedAt: null,
  };
}

export default function BookingEngineModal() {
  const {
    modals, closeModal, openModal,
    vehicles, rentals, maintHist, locations, extras, insurance,
    companySettings,
    addRental, addInvoice, addCustomer, setNotifications,
    nextContractId, nextInvoiceNum, showToast,
    groups, rateRules, promoCodes, setPromoCodes,
    areas, agreementTemplates,
    vehicleModels, resolveVehicle, effectiveConfirmation,
  } = useApp();

  const open = !!modals['booking-engine'];
  const [bk, setBk] = useState(initBk);
  const [photoIdx, setPhotoIdx] = useState({});   // { [vid]: currentIndex }
  const [vFilters, setVFilters] = useState({ type: 'all', seats: 'any', transmission: 'any', fuel: 'any' });
  const [confirmRef, setConfirmRef] = useState('');
  const [confirmRentalId, setConfirmRentalId] = useState(null);
  const [camera, setCamera] = useState(null); // { key, previewKey, label } when open
  const [countrySearch, setCountrySearch] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const countryRef = useRef(null);

  // Resolve which agreement template to use for a vehicle
  function getAgreementForVehicle(vid) {
    const v = vehicles.find(x => x.id === vid);
    if (!v) return null;
    if (v.agreementId) {
      const tmpl = agreementTemplates.find(t => t.id === v.agreementId);
      if (tmpl) return tmpl;
    }
    if (v.groupId) {
      const grp = groups.find(g => g.id === v.groupId);
      if (grp?.agreementId) {
        const tmpl = agreementTemplates.find(t => t.id === grp.agreementId);
        if (tmpl) return tmpl;
      }
    }
    return agreementTemplates.find(t => t.isDefault) || agreementTemplates[0] || null;
  }
  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);

  // Close country dropdown on outside click
  useEffect(() => {
    if (!countryOpen) return;
    function handleClick(e) {
      if (countryRef.current && !countryRef.current.contains(e.target)) setCountryOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [countryOpen]);

  if (!open) return null;

  // Returns the insurance plans to show for a given vehicle:
  // - If the vehicle's model has custom plans → use the model's plans
  // - Else if the vehicle itself has custom plans → use those
  // - Else fall back to global insurance plans
  function getVehicleInsurancePlans(vid) {
    const raw = vehicles.find(x => x.id === vid);
    if (!raw) return insurance;
    const v = resolveVehicle ? resolveVehicle(raw) : raw;
    if (v?.insuranceMode === 'custom' && v.customInsurance?.length > 0) return v.customInsurance;
    return insurance;
  }

  // Derived
  const pickLoc = locations.find(l => l.id === bk.pickupLocId) || locations[0];
  const retLoc  = locations.find(l => l.id === bk.returnLocId) || pickLoc;
  const vehicleInsPlans = getVehicleInsurancePlans(bk.vid);
  const selIns  = vehicleInsPlans.find(x => x.id === bk.selectedInsurance) || null;
  const selExtras = extras.filter(e => (bk.selectedExtras[e.id] || 0) > 0);

  function calcTotal(bkData) {
    const days = bkData.days || 1;
    const v = vehicles.find(x => x.id === bkData.vid);

    // Effective rate: use group price if vehicle uses group pricing
    let effectiveRate = bkData.rate || 0;
    if (v && v.useGroupPricing && v.groupId) {
      const g = groups.find(gg => gg.id === v.groupId);
      if (g) effectiveRate = g.priceDay || 0;
    }
    const rawBase = effectiveRate * days;

    // Apply matching rate rules
    const applicableRules = (rateRules || []).filter(r => {
      if (!r.active) return false;
      const appliesToV = r.appliesTo === 'all' || (v && r.appliesTo === v.groupId);
      if (!appliesToV) return false;
      const d1 = bkData.d1 ? new Date(bkData.d1) : new Date();
      if (r.type === 'duration') return days >= (r.minDays || 1);
      if (r.type === 'daterange') return d1 >= new Date(r.dateStart) && d1 <= new Date(r.dateEnd);
      if (r.type === 'both') return days >= (r.minDays || 1) && d1 >= new Date(r.dateStart) && d1 <= new Date(r.dateEnd);
      return false;
    });
    let ruleDiscount = 0, ruleSurcharge = 0;
    applicableRules.forEach(r => {
      const amt = r.valueType === 'percent' ? rawBase * r.value / 100 : r.value * days;
      if (r.effect === 'discount') ruleDiscount += amt;
      else ruleSurcharge += amt;
    });
    const base = Math.max(0, rawBase - ruleDiscount + ruleSurcharge);

    // Extras
    const extQtys = bkData.selectedExtras || {};
    const extAmt = extras
      .filter(e => (extQtys[e.id] || 0) > 0)
      .reduce((s, e) => s + (e.perDay ? e.price * days : e.price) * (extQtys[e.id] || 1), 0);

    // Insurance — look up in vehicle's custom plans first, then global
    const insAmt = (() => {
      const insPlans = getVehicleInsurancePlans(bkData.vid);
      const ins = insPlans.find(x => x.id === bkData.selectedInsurance);
      return ins ? (ins.price || 0) * days : 0;
    })();

    // Location fee
    const pl = locations.find(l => l.id === bkData.pickupLocId);
    const rl = locations.find(l => l.id === bkData.returnLocId);
    const locFee = (pl?.fee || 0) + (bkData.diffReturn && rl?.id !== bkData.pickupLocId ? (rl?.fee || 0) : 0);

    const subtotal = base + extAmt + insAmt + locFee;

    // Promo code
    let promoDiscount = 0, promoEntry = null;
    if (bkData.promoCode) {
      const pc = (promoCodes || []).find(p => p.code === bkData.promoCode.toUpperCase() && p.active);
      if (pc) {
        const expired = pc.expiryDate && new Date(pc.expiryDate) < new Date();
        const exhausted = pc.usageLimit && pc.usedCount >= pc.usageLimit;
        if (!expired && !exhausted) {
          promoEntry = pc;
          promoDiscount = pc.discountType === 'percent' ? subtotal * pc.discountValue / 100 : pc.discountValue;
          promoDiscount = Math.min(promoDiscount, subtotal);
        }
      }
    }

    const total = Math.max(0, subtotal - promoDiscount);
    return { effectiveRate, rawBase, ruleDiscount, ruleSurcharge, applicableRules, base, extAmt, insAmt, locFee, subtotal, promoDiscount, promoEntry, total };
  }

  function update(patch) {
    setBk(prev => {
      const next = { ...prev, ...patch };
      if (patch.d1 !== undefined || patch.d2 !== undefined) {
        const d1 = new Date(next.d1), d2 = new Date(next.d2);
        next.days = Math.max(1, Math.round((d2 - d1) / 86400000));
      }
      return next;
    });
  }

  function selectCountry(c) {
    update({ country: c.code, countryName: c.name, countryFlag: c.flag });
    setCountrySearch(c.flag + ' ' + c.name);
    setCountryOpen(false);
    // Clear phone if it only had a previous dial code (prefix is now shown visually)
    setBk(prev => {
      const trimmed = (prev.phone || '').trim();
      if (countries.some(x => trimmed === x.dial || trimmed === x.dial + ' ')) {
        return { ...prev, phone: '' };
      }
      return prev;
    });
  }

  // Customer requirement helpers
  const req = (key) => companySettings[key] || 'hidden';
  const reqVisible = (key) => req(key) !== 'hidden';
  const reqRequired = (key) => req(key) === 'required';

  // Are any detail fields (passport/licence/expiry/photos) visible?
  const anyDetailVisible = reqVisible('reqPassportNum') || reqVisible('reqLicenceNum') || reqVisible('reqLicExpiry') || reqVisible('reqPassportPhoto') || reqVisible('reqLicencePhoto');
  // Is the agreement visible?
  const agreementVisible = reqVisible('reqSignAgreement');
  // Should Step 4 be shown at all? Always show if name/phone/email are needed (they always are), so skip only if nothing configurable is visible
  // Step 4 always shows (name/phone/email are required) — only the extra document fields are conditional
  const skipStep4 = false;

  function goStep(n) {
    // Validate
    if (n === 2) {
      if (!bk.d1 || !bk.d2 || new Date(bk.d2) <= new Date(bk.d1)) {
        showToast('Please select valid dates'); return;
      }
      // Min advance booking check
      const minHrs = companySettings.minAdvanceHours ?? 6;
      if (minHrs > 0) {
        const pickupDT = new Date(bk.d1 + 'T' + (bk.t1 || '10:00'));
        const minTime = new Date(Date.now() + minHrs * 3600000);
        if (pickupDT < minTime) {
          showToast(`Booking must be at least ${minHrs} hours in advance`); return;
        }
      }
      // Max advance booking check
      const maxDays = companySettings.maxAdvanceDays ?? 90;
      const pickupDate = new Date(bk.d1);
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + maxDays);
      if (pickupDate > maxDate) {
        showToast(`Cannot book more than ${maxDays} days in advance`); return;
      }
    }
    if (n === 3) {
      if (!bk.vid) { showToast('Please select a vehicle'); return; }
    }
    // Skip Step 4 if all configurable fields are hidden
    if (n === 4 && skipStep4) { n = 5; }
    if (n === 5) {
      if (!bk.fname || !bk.phone || !bk.email) {
        showToast('Please fill in name, phone and email'); return;
      }
      if (bk.phone.trim().length < 8) {
        showToast('Please enter a valid phone number'); return;
      }
      if (!bk.termsAgreed) {
        showToast('Please agree to the Terms & Conditions'); return;
      }
      // Validate required customer requirement fields
      if (reqRequired('reqPassportNum') && !bk.passport) { showToast('Passport / ID number is required'); return; }
      if (reqRequired('reqLicenceNum') && !bk.licence) { showToast('Driving licence number is required'); return; }
      if (reqRequired('reqLicExpiry') && !bk.licExpiry) { showToast('Licence expiry date is required'); return; }
      if (reqRequired('reqPassportPhoto') && !bk.idFile) { showToast('Passport / ID photo is required'); return; }
      if (reqRequired('reqLicencePhoto') && !bk.licenceFile) { showToast('Licence photo is required'); return; }
      if (reqRequired('reqSignAgreement') && !bk.presigned) { showToast('You must sign the rental agreement to continue'); return; }
    }
    setBk(prev => ({ ...prev, step: n }));
  }

  function close() {
    closeModal('booking-engine');
    setBk(initBk());
    setCountrySearch('');
    setCountryOpen(false);
  }

  function pickVehicle(vid) {
    const rawV = vehicles.find(x => x.id === vid);
    if (!rawV) return;
    // Resolve model-inherited fields (photos, price, etc.)
    const v = resolveVehicle ? resolveVehicle(rawV) : rawV;
    // Use model price first; fall back to group price; then individual vehicle price
    let rate = v.priceDay || 0;
    if (!rawV.modelId && v.useGroupPricing && v.groupId) {
      const g = groups.find(gg => gg.id === v.groupId);
      if (g) rate = g.priceDay || 0;
    }
    // Display name: prefer model name (hides plate) when assigned to a model
    const displayName = rawV.modelId && v._modelName ? v._modelName : (rawV.name?.split('–')[0]?.trim() || rawV.name || 'Vehicle');
    setBk(prev => {
      const next = { ...prev, vid, vehName: displayName, vehIcon: v.icon || '🚗', rate, selectedInsurance: null };
      next.basePrice = next.rate * next.days;
      return next;
    });
    setTimeout(() => setBk(prev => ({ ...prev, step: 3 })), 300);
  }

  function toggleExtra(id) {
    // For non-qty extras: toggle 0/1
    setBk(prev => {
      const cur = prev.selectedExtras[id] || 0;
      return { ...prev, selectedExtras: { ...prev.selectedExtras, [id]: cur > 0 ? 0 : 1 } };
    });
  }

  function setExtraQty(id, qty, maxQty) {
    const clamped = Math.max(0, Math.min(qty, maxQty || 10));
    setBk(prev => ({ ...prev, selectedExtras: { ...prev.selectedExtras, [id]: clamped } }));
  }

  function selectInsurance(id) {
    setBk(prev => ({ ...prev, selectedInsurance: prev.selectedInsurance === id ? null : id }));
  }

  function selectPayOpt(method) {
    setBk(prev => ({ ...prev, payMethod: method }));
  }

  // Canvas signature helpers
  function getCanvasPos(e, c) {
    const r = c.getBoundingClientRect(), src = e.touches ? e.touches[0] : e;
    return [(src.clientX - r.left) * (c.width / r.width), (src.clientY - r.top) * (c.height / r.height)];
  }
  function canvasStart(e) {
    e.preventDefault();
    const c = canvasRef.current; if (!c) return;
    drawingRef.current = true;
    const ctx = c.getContext('2d');
    ctx.strokeStyle = '#1A1916'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.beginPath(); const [x, y] = getCanvasPos(e, c); ctx.moveTo(x, y);
  }
  function canvasMove(e) {
    e.preventDefault();
    if (!drawingRef.current) return;
    const c = canvasRef.current; if (!c) return;
    const [x, y] = getCanvasPos(e, c);
    const ctx = c.getContext('2d'); ctx.lineTo(x, y); ctx.stroke();
  }
  function canvasStop() { drawingRef.current = false; }
  function clearCanvas() {
    const c = canvasRef.current; if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
  }
  function saveSignature() {
    const c = canvasRef.current; if (!c) { showToast('Signature canvas not ready'); return; }
    const px = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    if (!px.some((v, i) => i % 4 === 3 && v > 10)) { showToast('Please draw your signature first'); return; }
    const dataUrl = c.toDataURL('image/png');
    update({ presigned: true, presignData: dataUrl, presignedAt: new Date().toLocaleString('en-GB') });
    showToast('✅ Agreement pre-signed!');
  }

  function submit() {
    if (!bk.fname || !bk.phone || !bk.email) {
      showToast('Please fill in name, phone and email'); return;
    }
    const v = vehicles.find(x => x.id === bk.vid);
    if (!v) { showToast('Please select a vehicle'); return; }

    const fullName = [bk.fname, bk.lname].filter(Boolean).join(' ') || 'Online Customer';
    const newRef   = 'BK-' + Date.now().toString().slice(-4);
    const cid      = nextContractId();
    const pl       = locations.find(l => l.id === bk.pickupLocId) || locations[0];
    const rl       = locations.find(l => l.id === bk.returnLocId) || pl;
    const { base, extAmt, insAmt, locFee, total, promoEntry, ruleDiscount, ruleSurcharge } = calcTotal(bk);
    const submitVatMode  = companySettings?.vatMode || 'inclusive';
    const submitVatRate  = parseFloat(companySettings?.vatRate || 19);
    const submitVatAmt   = submitVatMode === 'exclusive'
      ? +(total * submitVatRate / 100).toFixed(2)
      : +(total - total / (1 + submitVatRate / 100)).toFixed(2);
    const submitGrandTotal = submitVatMode === 'exclusive' ? +(total + submitVatAmt).toFixed(2) : total;
    const submitInvSubtotal = submitVatMode === 'exclusive'
      ? total
      : +(total / (1 + submitVatRate / 100)).toFixed(2);

    const d1 = new Date(bk.d1 + 'T' + bk.t1);
    const d2 = new Date(bk.d2 + 'T' + bk.t2);
    const startLabel = d1.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const endLabel   = d2.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    // Generate invoice number early so we can link it to the rental
    const invNum = nextInvoiceNum();

    const newRental = {
      id: cid, vid: bk.vid, status: 'booked',
      customer: fullName, phone: (bk.country ? (countries.find(x => x.code === bk.country)?.dial + ' ' ) : '') + bk.phone, email: bk.email,
      passport: bk.passport, licence: bk.licence, licExpiry: bk.licExpiry,
      nationality: bk.countryName,
      start: bk.d1 + 'T' + bk.t1, end: bk.d2 + 'T' + bk.t2,
      t1: bk.t1, t2: bk.t2,
      startLabel, endLabel,
      priceType: 'day', rate: bk.rate,
      discount: +(ruleDiscount - ruleSurcharge + (promoEntry ? (promoEntry.discountType === 'percent' ? (base + extAmt + insAmt + locFee) * promoEntry.discountValue / 100 : promoEntry.discountValue) : 0)).toFixed(2),
      promoCode: promoEntry?.code || null,
      agreedPrice: submitGrandTotal,
      baseRentalPrice: +base.toFixed(2),
      deposit: parseFloat(selIns?.deposit) || 0,
      payMethod: bk.payMethod,
      pickupLocId: bk.pickupLocId, pickupLocName: pl?.name || '—',
      returnLocId: bk.diffReturn ? bk.returnLocId : bk.pickupLocId,
      returnLocName: bk.diffReturn ? rl?.name : pl?.name,
      extras: selExtras.map(e => e.name),
      selectedExtras: bk.selectedExtras || {},   // qty map — lets check-in diff added extras accurately
      insurance: selIns?.name || null,
      insuranceId: bk.selectedInsurance || null,
      condPre: null, condPreNote: '', condRet: null, condRetNote: '',
      photosPre: [], photosRet: [],
      notes: bk.notes || ('Online booking. Ref: ' + newRef),
      agreement: bk.presigned ? 'online' : null,
      signed: bk.presigned,
      presigned: bk.presigned,
      presignData: bk.presignData || null,
      presignedAt: bk.presignedAt || null,
      agreementSaved: bk.presigned ? (() => {
        const tmpl = getAgreementForVehicle(bk.vid);
        const v2 = vehicles.find(x => x.id === bk.vid);
        const ctx = buildContractContext({
          rental: {
            id: newRef,
            customer: `${bk.fname || ''} ${bk.lname || ''}`.trim(),
            customerCompany: bk.customerCompany || '',
            passport: bk.passport, licence: bk.licence, licExpiry: bk.licExpiry,
            email: bk.email,
            phone: (bk.country ? (countries.find(x => x.code === bk.country)?.dial + ' ') : '') + (bk.phone || ''),
            nationality: bk.countryName,
            startLabel, endLabel, t1: bk.t1, t2: bk.t2,
            pickupLocName: pl?.name, returnLocName: bk.diffReturn ? rl?.name : pl?.name,
            calSpan: bk.days, rate: bk.rate,
            deposit: parseFloat(selIns?.deposit) || 0, depositMethod: bk.payMethod,
            agreedPrice: submitGrandTotal,
            insurance: selIns?.name, insuranceExcess: selIns?.excess,
            extras: selExtras.map(e => e.name),
          },
          vehicle: v2,
          company: companySettings,
          signatureDataUrl: bk.presignData,
          signedAt: new Date().toISOString(),
        });
        return {
          templateId: tmpl?.id || null,
          templateName: tmpl?.name || 'Rental Agreement',
          body: tmpl?.body || '',
          renderedHtml: tmpl ? renderContract(tmpl, ctx) : '',
          context: ctx,
          signedAt: new Date().toISOString(),
          signedAtFormatted: bk.presignedAt || new Date().toLocaleString('en-GB'),
          customerName: `${bk.fname || ''} ${bk.lname || ''}`.trim(),
          vehicleName: v2?.name || '',
          vehiclePlate: v2?.plate || '',
          signData: bk.presignData,
        };
      })() : null,
      licencePhoto: bk.licencePreview || null,
      passportPhoto: bk.idPreview    || null,
      invoiceId: invNum,
      calStart: d1.getDate(), calSpan: bk.days,
      createdAt: new Date().toISOString().slice(0, 10),
      source: 'online', bookingRef: newRef,
      confirmed: (() => {
        const rawV = vehicles.find(x => x.id === bk.vid);
        return effectiveConfirmation ? effectiveConfirmation(rawV) === 'auto' : false;
      })(),
      bankTransferPending: bk.payMethod === 'bank',
    };

    addRental(newRental);

    // Mark promo code as used
    if (promoEntry) {
      setPromoCodes(prev => prev.map(p => p.id === promoEntry.id ? { ...p, usedCount: (p.usedCount || 0) + 1 } : p));
    }

    // Auto-create invoice — use calcRental for consistent ex-VAT line items
    const invoiceCalc = calcRental({
      rate:          base / bk.days,
      days:          bk.days,
      discount:      0,
      discType:      'eur',
      selectedIns:   selIns,
      extrasConfig:  extras,
      selectedExtras: bk.selectedExtras || {},
      locFee,
      vatMode:       submitVatMode,
      vatRate:       submitVatRate / 100,
      vehicleName:   v.name,
    });
    const invLines = [...invoiceCalc.lines];
    if (promoEntry) {
      const f = submitVatMode === 'inclusive' ? 1 / (1 + submitVatRate / 100) : 1;
      const promoAmt = promoEntry.discountType === 'percent'
        ? invoiceCalc.subtotal * f * promoEntry.discountValue / 100
        : promoEntry.discountValue * f;
      invLines.push({ desc: `Promo code — ${promoEntry.code}`, qty: 1, unit: -+promoAmt.toFixed(2), total: -+promoAmt.toFixed(2) });
    }
    const pmLabels = { stripe: 'Stripe · Card (Online)', bank: 'Bank Transfer', cash: 'Cash on pickup' };
    addInvoice({
      id: invNum, num: invNum, customer: fullName, email: bk.email, addr: 'Cyprus',
      vehicle: v.name, contract: cid, vid: bk.vid,
      method: bk.payMethod, methodLabel: pmLabels[bk.payMethod] || bk.payMethod,
      issue: startLabel, due: endLabel, issueDate: bk.d1, dueDate: bk.d2,
      lines: invLines, subtotal: submitInvSubtotal, vatRate: submitVatRate, vatAmt: submitVatAmt, discount: 0, discType: 'eur',
      total: submitGrandTotal,
      notes: 'Online booking. Ref: ' + newRef,
      status: bk.payMethod === 'stripe' ? 'paid' : bk.payMethod === 'bank' ? 'pending' : 'draft',
      source: 'online',
    });

    // Auto-create/update customer
    const existingCust = window._bookingEngineCustomers?.find(c =>
      (c.fname + ' ' + c.lname).trim() === fullName.trim() || c.email === bk.email
    );
    if (!existingCust) {
      const colors = ['#C8401E','#1C5FAD','#1A7A4A','#5B3E9E','#B8720A','#6B6760','#1A3A5C'];
      const parts = fullName.trim().split(' ');
      addCustomer({
        id: 'C-' + Date.now().toString(36).slice(-6),
        fname: parts[0] || fullName, lname: parts.slice(1).join(' ') || '',
        phone: (bk.country ? (countries.find(x => x.code === bk.country)?.dial + ' ') : '') + bk.phone, email: bk.email, address: '',
        passport: bk.passport, licence: bk.licence, licExpiry: bk.licExpiry,
        nationality: bk.countryName,
        since: new Date().toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        notes: 'Created from online booking ' + newRef,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    // Notification
    setNotifications(prev => [{
      id: 'N-bk-' + newRef, type: 'incoming', icon: '🌐',
      title: 'New online booking — ' + newRef,
      body: fullName + ' · ' + v.name + ' · ' + startLabel + ' → ' + endLabel,
      time: 'just now', read: false, link: 'incoming',
    }, ...prev]);

    setConfirmRef(newRef);
    setConfirmRentalId(cid);
    setBk(prev => ({ ...prev, step: 6 }));
    showToast('✓ Booking confirmed — ' + newRef);
  }

  const ct = calcTotal(bk);
  const { base, extAmt, insAmt, locFee, total } = ct;

  // VAT mode: 'inclusive' = price already contains VAT, 'exclusive' = VAT added on top
  const vatMode    = companySettings?.vatMode || 'inclusive';
  const vatRateVal = parseFloat(companySettings?.vatRate || 19);
  const vatAmt     = vatMode === 'exclusive'
    ? +(total * vatRateVal / 100).toFixed(2)
    : +(total - total / (1 + vatRateVal / 100)).toFixed(2);
  const grandTotal = vatMode === 'exclusive' ? +(total + vatAmt).toFixed(2) : total;

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const modalStyle = {
    width: 760, maxWidth: '96vw', maxHeight: '92vh', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    background: 'var(--sur)', borderRadius: 'var(--rl)',
    boxShadow: '0 20px 60px rgba(0,0,0,.3)',
  };

  return (
  <>
    <div style={overlayStyle} onClick={close}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--bd)', flexShrink: 0 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bl-l)', border: '1px solid var(--bl-b)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--bl)', fontSize: 16 }}>🌐</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Booking Page Preview</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)' }}>fleetrent.app/book/{companySettings.slug || 'democyprus'}</div>
          </div>
          <button onClick={close} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--tx3)', lineHeight: 1 }}>×</button>
        </div>

        {/* Step progress bar */}
        <div style={{ display: 'flex', background: 'var(--sur2)', borderBottom: '1px solid var(--bd)', flexShrink: 0 }}>
          {STEPS.map(s => {
            const isDone   = bk.step > s.n;
            const isActive = bk.step === s.n;
            return (
              <div key={s.n}
                onClick={() => { if (isDone && s.n < 6 && !(s.n === 4 && skipStep4)) goStep(s.n); }}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 5, padding: '9px 4px', fontSize: 10, fontWeight: 500, cursor: isDone ? 'pointer' : 'default',
                  color: isActive ? 'var(--ac)' : isDone ? 'var(--gr)' : 'var(--tx3)',
                  borderBottom: isActive ? '2px solid var(--ac)' : isDone ? '2px solid var(--gr)' : '2px solid transparent',
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 700, flexShrink: 0,
                  background: isActive ? 'var(--ac)' : isDone ? 'var(--gr)' : 'var(--bd)',
                  color: (isActive || isDone) ? '#fff' : 'var(--tx3)',
                }}>
                  {isDone ? '✓' : s.n}
                </div>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>

          {/* STEP 1: Dates & Location */}
          {bk.step === 1 && (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{companySettings.name || 'FleetRent'}</div>
                  <div style={{ fontSize: 12, color: 'var(--tx3)' }}>Vehicle & Equipment Rental</div>
                </div>
                {/* Already booked? → public Manage Booking portal */}
                <a href="/manage" target="_blank" rel="noopener" style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 11, fontWeight: 600, padding: '6px 12px',
                  background: 'var(--sur2)', border: '1px solid var(--bd)',
                  borderRadius: 20, color: 'var(--tx2)', textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }} title="Manage an existing booking">
                  📋 Manage your booking
                </a>
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Select your rental dates</div>

              {/* Date range picker */}
              <div style={{ marginBottom: 12 }}>
                <DateRangePicker
                  startDate={bk.d1}
                  endDate={bk.d2}
                  onChange={(d1, d2) => {
                    const days = d1 && d2 ? Math.max(1, Math.round((new Date(d2) - new Date(d1)) / 86400000)) : bk.days;
                    update({ d1: d1 || bk.d1, d2: d2 || bk.d2, ...(d1 && d2 ? { days } : {}) });
                  }}
                  labelStart="Pickup Date"
                  labelEnd="Return Date"
                />
              </div>

              {/* Time selectors */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Pickup Time', key: 't1', val: bk.t1 },
                  { label: 'Return Time', key: 't2', val: bk.t2 },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>{f.label}</label>
                    <select className="fsel2" value={f.val} onChange={e => update({ [f.key]: e.target.value })} style={{ fontSize: 13 }}>
                      {TIMES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              {(() => {
                const fmtFee = (l) => l.fee === 0 ? 'Free' : l.fee == null ? 'On request' : '€' + l.fee + ' fee';
                const pickVal = bk.pickupLocId || (locations[0] && locations[0].id) || '';
                return (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Pickup location</div>
                    <select
                      className="fi"
                      value={pickVal}
                      onChange={e => update({ pickupLocId: e.target.value })}
                      style={{ width: '100%', marginBottom: 10 }}
                    >
                      {locations.map(l => (
                        <option key={l.id} value={l.id}>
                          {(l.icon || '📍') + ' ' + l.name + ' — ' + fmtFee(l)}
                        </option>
                      ))}
                    </select>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <input
                        type="checkbox"
                        checked={bk.diffReturn}
                        onChange={e => update({ diffReturn: e.target.checked, returnLocId: e.target.checked ? (bk.returnLocId || pickVal) : null })}
                        style={{ accentColor: 'var(--ac)' }}
                      />
                      <label style={{ fontSize: 12, cursor: 'pointer' }} onClick={() => update({ diffReturn: !bk.diffReturn, returnLocId: !bk.diffReturn ? (bk.returnLocId || pickVal) : null })}>
                        Return at a different location
                      </label>
                    </div>

                    {bk.diffReturn && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Return location</div>
                        <select
                          className="fi"
                          value={bk.returnLocId || ''}
                          onChange={e => update({ returnLocId: e.target.value })}
                          style={{ width: '100%' }}
                        >
                          <option value="" disabled>Select return location…</option>
                          {locations.map(l => (
                            <option key={l.id} value={l.id}>
                              {(l.icon || '📍') + ' ' + l.name + ' — ' + fmtFee(l)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Advance booking warnings */}
              {bk.d1 && bk.t1 && (() => {
                const minHrs = companySettings.minAdvanceHours ?? 6;
                const maxDays = companySettings.maxAdvanceDays ?? 90;
                const pickupDT = new Date(bk.d1 + 'T' + (bk.t1 || '10:00'));
                const minTime = new Date(Date.now() + minHrs * 3600000);
                const maxDate = new Date();
                maxDate.setDate(maxDate.getDate() + maxDays);
                const tooSoon = minHrs > 0 && pickupDT < minTime;
                const tooFar = new Date(bk.d1) > maxDate;
                if (!tooSoon && !tooFar) return null;
                return (
                  <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 'var(--r)', marginTop: 4, background: 'var(--ac-l)', border: '1px solid var(--ac-b)', color: 'var(--ac)' }}>
                    {tooSoon && `⚠️ Booking must be at least ${minHrs} hours in advance`}
                    {tooFar && `⚠️ Cannot book more than ${maxDays} days in advance`}
                  </div>
                );
              })()}

              {/* Availability msg */}
              {bk.d1 && bk.d2 && new Date(bk.d2) > new Date(bk.d1) && (() => {
                const avail = vehicles.filter(v => v.bookPage && !maintHist.some(m => m.vid === v.id && m.status === 'in_progress') && isAvailable(v.id, bk.d1, bk.d2, rentals, companySettings.bufferHours ?? 12));
                const ok = avail.length > 0;
                return (
                  <div style={{
                    fontSize: 12, padding: '8px 12px', borderRadius: 'var(--r)', marginTop: 4,
                    background: ok ? 'var(--gr-l)' : 'var(--ac-l)',
                    border: `1px solid ${ok ? 'var(--gr-b)' : 'var(--ac-b)'}`,
                    color: ok ? 'var(--gr)' : 'var(--ac)',
                  }}>
                    {ok
                      ? `✓ ${avail.length} vehicle${avail.length !== 1 ? 's' : ''} available for ${bk.days} day${bk.days !== 1 ? 's' : ''}`
                      : 'No vehicles available for these dates — try different dates'}
                  </div>
                );
              })()}
              {bk.d1 && bk.d2 && new Date(bk.d2) <= new Date(bk.d1) && (
                <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 'var(--r)', marginTop: 4, background: 'var(--ac-l)', border: '1px solid var(--ac-b)', color: 'var(--ac)' }}>
                  ⚠️ Return date must be after pickup date
                </div>
              )}

              <button className="btn btn-primary" onClick={() => goStep(2)}
                style={{ width: '100%', marginTop: 14, justifyContent: 'center', fontSize: 13 }}>
                See Available Vehicles →
              </button>
            </div>
          )}

          {/* STEP 2: Choose Vehicle */}
          {bk.step === 2 && (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Available Vehicles</div>
                <div style={{ fontSize: 11, color: 'var(--tx3)' }}>{bk.d1} {bk.t1} → {bk.d2} {bk.t2} ({bk.days} days)</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 14 }}>
                📍 Pickup: {pickLoc?.name}{bk.diffReturn && retLoc?.id !== pickLoc?.id ? ' · Return: ' + retLoc?.name : ''}
              </div>

              {/* ── Sidebar + Grid layout ───────────────────────────────── */}
              {(() => {
                // Derive dynamic type options from fleet
                const fleetTypes = ['all', ...new Set(vehicles.filter(v => v.bookPage).map(v => v.type).filter(Boolean))];
                // Derive seat options from fleet
                const maxSeats = Math.max(...vehicles.filter(v => v.bookPage && parseInt(v.seats)).map(v => parseInt(v.seats)), 0);

                // Resolve pickup area from selected location
                const pickupArea = (() => {
                  const loc = locations.find(l => l.id === bk.pickupLocId);
                  return loc?.areaId || null;
                })();

                // Predict where a vehicle will be at a given date based on future drop-offs
                const predictArea = (vid) => {
                  // Find all booked/active rentals for this vehicle that end before or on the pickup date
                  const futureDropoffs = rentals
                    .filter(r => r.vid === vid && ['booked', 'active', 'overdue'].includes(r.status) && r.end && r.returnLocId)
                    .filter(r => new Date(r.end) <= new Date(bk.d1))
                    .sort((a, b) => new Date(b.end) - new Date(a.end)); // latest first

                  if (futureDropoffs.length > 0) {
                    // The last drop-off before pickup date determines where the car will be
                    const lastDropoff = futureDropoffs[0];
                    const dropLoc = locations.find(l => l.id === lastDropoff.returnLocId);
                    return dropLoc?.areaId || null;
                  }
                  // No future drop-offs — use current area
                  return vid ? (vehicles.find(vv => vv.id === vid)?.currentAreaId || null) : null;
                };

                // Apply all filters — only show available vehicles in the pickup area
                const filtered = vehicles.filter(rawV => {
                  // Resolve model-inherited fields for filtering (type, transmission, fuel, seats)
                  const v = resolveVehicle ? resolveVehicle(rawV) : rawV;
                  if (!rawV.bookPage) return false;
                  // Block vehicles in maintenance
                  if (maintHist.some(m => m.vid === rawV.id && m.status === 'in_progress')) return false;
                  if (!isAvailable(rawV.id, bk.d1, bk.d2, rentals, companySettings.bufferHours ?? 12)) return false;
                  // Check min days: use group's minDays if vehicle uses group pricing, otherwise vehicle's own
                  const grpMinDays = (v.useGroupPricing && v.groupId) ? (groups.find(gg => gg.id === v.groupId)?.minDays ?? 1) : 1;
                  const effectiveMinDays = Math.max(v.minDays || 1, grpMinDays);
                  if (bk.days < effectiveMinDays) return false;
                  // Area filter: vehicle must be in the pickup area at the pickup date
                  if (pickupArea) {
                    const vehicleAreaAtPickup = predictArea(rawV.id);
                    if (vehicleAreaAtPickup && vehicleAreaAtPickup !== pickupArea) return false;
                  }
                  if (vFilters.type !== 'all' && v.type !== vFilters.type) return false;
                  if (vFilters.seats !== 'any') {
                    const s = parseInt(v.seats) || 0;
                    if (s < parseInt(vFilters.seats)) return false;
                  }
                  if (vFilters.transmission !== 'any' && v.transmission !== vFilters.transmission) return false;
                  if (vFilters.fuel !== 'any' && v.fuelType !== vFilters.fuel) return false;
                  return true;
                });

                // ── Group filtered vehicles by model ───────────────────────
                // Result: array of { type: 'model'|'vehicle', model?, vehicles, totalInModel? }
                const groupedItems = [];
                const seenModels = new Set();
                filtered.forEach(rawV => {
                  if (rawV.modelId) {
                    if (seenModels.has(rawV.modelId)) return;
                    const model = (vehicleModels || []).find(mm => mm.id === rawV.modelId);
                    if (!model) {
                      // Model deleted but vehicle still references it — show as individual
                      groupedItems.push({ type: 'vehicle', vehicle: rawV });
                      return;
                    }
                    const groupVehicles = filtered.filter(vv => vv.modelId === rawV.modelId);
                    const totalInModel = vehicles.filter(vv => vv.modelId === rawV.modelId).length;
                    groupedItems.push({ type: 'model', model, vehicles: groupVehicles, totalInModel });
                    seenModels.add(rawV.modelId);
                  } else {
                    groupedItems.push({ type: 'vehicle', vehicle: rawV });
                  }
                });

                const activeFilters = Object.values(vFilters).filter(v => v !== 'all' && v !== 'any').length;

                return (
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

                    {/* Filter sidebar */}
                    <div style={{ width: 160, flexShrink: 0, border: '1px solid var(--bd)', borderRadius: 12, padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Filters
                        {activeFilters > 0 && (
                          <button onClick={() => setVFilters({ type: 'all', seats: 'any', transmission: 'any', fuel: 'any' })}
                            style={{ fontSize: 10, color: 'var(--ac)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            Reset
                          </button>
                        )}
                      </div>

                      {/* Body type */}
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Body type</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {fleetTypes.map(t => (
                            <div key={t} onClick={() => setVFilters(f => ({ ...f, type: t }))}
                              style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, background: vFilters.type === t ? 'var(--ac-l)' : 'transparent' }}>
                              <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${vFilters.type === t ? 'var(--ac)' : 'var(--bd)'}`, background: vFilters.type === t ? 'var(--ac)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {vFilters.type === t && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />}
                              </div>
                              <span style={{ fontSize: 11, color: vFilters.type === t ? 'var(--ac)' : 'var(--tx)', fontWeight: vFilters.type === t ? 600 : 400 }}>
                                {t === 'all' ? 'All types' : t}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Seats */}
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Seats</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {[['any','Any'], ['2','2+'], ['4','4+'], ['5','5+'], ['7','7+'], ['9','9+']].filter(([val]) => val === 'any' || maxSeats >= parseInt(val)).map(([val, label]) => (
                            <div key={val} onClick={() => setVFilters(f => ({ ...f, seats: val }))}
                              style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, background: vFilters.seats === val ? 'var(--ac-l)' : 'transparent' }}>
                              <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${vFilters.seats === val ? 'var(--ac)' : 'var(--bd)'}`, background: vFilters.seats === val ? 'var(--ac)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {vFilters.seats === val && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />}
                              </div>
                              <span style={{ fontSize: 11, color: vFilters.seats === val ? 'var(--ac)' : 'var(--tx)', fontWeight: vFilters.seats === val ? 600 : 400 }}>{label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Transmission */}
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transmission</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {[['any','Any'], ['automatic','Automatic'], ['manual','Manual']].map(([val, label]) => (
                            <div key={val} onClick={() => setVFilters(f => ({ ...f, transmission: val }))}
                              style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, background: vFilters.transmission === val ? 'var(--ac-l)' : 'transparent' }}>
                              <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${vFilters.transmission === val ? 'var(--ac)' : 'var(--bd)'}`, background: vFilters.transmission === val ? 'var(--ac)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {vFilters.transmission === val && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />}
                              </div>
                              <span style={{ fontSize: 11, color: vFilters.transmission === val ? 'var(--ac)' : 'var(--tx)', fontWeight: vFilters.transmission === val ? 600 : 400 }}>{label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Fuel */}
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fuel</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {[['any','Any'], ['petrol','Petrol'], ['diesel','Diesel'], ['electric','Electric'], ['hybrid','Hybrid']].map(([val, label]) => (
                            <div key={val} onClick={() => setVFilters(f => ({ ...f, fuel: val }))}
                              style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, background: vFilters.fuel === val ? 'var(--ac-l)' : 'transparent' }}>
                              <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${vFilters.fuel === val ? 'var(--ac)' : 'var(--bd)'}`, background: vFilters.fuel === val ? 'var(--ac)' : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {vFilters.fuel === val && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />}
                              </div>
                              <span style={{ fontSize: 11, color: vFilters.fuel === val ? 'var(--ac)' : 'var(--tx)', fontWeight: vFilters.fuel === val ? 600 : 400 }}>{label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Vehicle / Model grid */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: groupedItems.length > 0 ? 'var(--tx3)' : 'var(--ac)', marginBottom: 10 }}>
                        {groupedItems.length > 0
                          ? `${groupedItems.length} option${groupedItems.length !== 1 ? 's' : ''} available`
                          : 'No vehicles available for these dates — try different dates or adjust filters'}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
                        {groupedItems.map((item, itemIdx) => {
                    // Resolve a "view object" v that represents the card data
                    // For models: use the model + first available vehicle in the group
                    // For individuals: resolve from the vehicle itself
                    const isModelGroup = item.type === 'model';
                    const firstAvailable = isModelGroup ? item.vehicles[0] : item.vehicle;
                    const v = isModelGroup ? {
                      ...firstAvailable,
                      id: firstAvailable.id,
                      name: item.model.name,                       // ← model name shown (no plate)
                      icon: item.model.icon || firstAvailable.icon,
                      photos: item.model.photos || [],
                      features: item.model.features,
                      transmission: item.model.transmission,
                      fuelType: item.model.fuelType,
                      seats: item.model.seats,
                      mileage: item.model.mileage,
                      priceDay: item.model.priceDay,
                      type: item.model.type,
                      groupId: item.model.groupId,
                      useGroupPricing: false,                      // model price already final
                    } : (() => {
                      // Individual vehicle — strip plate from name to hide registration
                      const resolved = resolveVehicle ? resolveVehicle(firstAvailable) : firstAvailable;
                      return {
                        ...resolved,
                        // Strip plate (after dash) for display
                        name: (resolved.name?.split('–')[0]?.trim() || resolved.name || 'Vehicle'),
                      };
                    })();

                    const cardKey = isModelGroup ? 'm:' + item.model.id : 'v:' + item.vehicle.id;
                    const isSel = isModelGroup
                      ? item.vehicles.some(vv => vv.id === bk.vid)
                      : bk.vid === item.vehicle.id;
                    const availabilityCount = isModelGroup ? item.vehicles.length : null;
                    const totalCount = isModelGroup ? item.totalInModel : null;
                    const grp = v.groupId ? groups.find(gg => gg.id === v.groupId) : null;
                    let price = v.priceDay || 0;
                    if (isModelGroup) {
                      // Model price wins (already set above via item.model.priceDay)
                      price = parseFloat(item.model.priceDay) || price;
                    } else if (v.useGroupPricing && grp) {
                      price = grp.priceDay || 0;
                    }
                    const rawTotal = price * bk.days;
                    const vRules = (rateRules || []).filter(r => {
                      if (!r.active) return false;
                      const ok = r.appliesTo === 'all' || r.appliesTo === v.groupId;
                      if (!ok) return false;
                      const d1 = bk.d1 ? new Date(bk.d1) : new Date();
                      if (r.type === 'duration') return bk.days >= (r.minDays || 1);
                      if (r.type === 'daterange') return d1 >= new Date(r.dateStart) && d1 <= new Date(r.dateEnd);
                      if (r.type === 'both') return bk.days >= (r.minDays || 1) && d1 >= new Date(r.dateStart) && d1 <= new Date(r.dateEnd);
                      return false;
                    });
                    let vDiscount = 0, vSurcharge = 0;
                    vRules.forEach(r => {
                      const amt = r.valueType === 'percent' ? rawTotal * r.value / 100 : r.value * bk.days;
                      if (r.effect === 'discount') vDiscount += amt; else vSurcharge += amt;
                    });
                    const discountedTotal = Math.max(0, rawTotal - vDiscount + vSurcharge);
                    const hasRules = vRules.length > 0;
                    return (
                      <div key={cardKey}
                        onClick={() => pickVehicle(firstAvailable.id)}
                        style={{
                          border: `2px solid ${isSel ? 'var(--ac)' : 'var(--bd)'}`,
                          borderRadius: 14, overflow: 'hidden',
                          cursor: 'pointer',
                          background: 'var(--sur)',
                          boxShadow: isSel ? '0 0 0 3px var(--ac-l)' : 'var(--sh)',
                          transition: 'all .14s',
                          display: 'flex', flexDirection: 'column',
                        }}>

                        {/* Photo carousel */}
                        {(() => {
                          const photos = v.photos?.filter(Boolean) || [];
                          const cur = photoIdx[cardKey] || 0;
                          const total = photos.length;
                          function prevPhoto(e) {
                            e.stopPropagation();
                            setPhotoIdx(p => ({ ...p, [cardKey]: (cur - 1 + total) % total }));
                          }
                          function nextPhoto(e) {
                            e.stopPropagation();
                            setPhotoIdx(p => ({ ...p, [cardKey]: (cur + 1) % total }));
                          }
                          return (
                            <div style={{ height: 150, background: 'var(--sur3)', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                              {total > 0
                                ? <img src={photos[cur]} alt={`${v.name} ${cur + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity .15s' }} />
                                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 52 }}>{v.icon || '🚗'}</div>
                              }

                              {/* Prev / Next arrows */}
                              {total > 1 && (
                                <>
                                  <button onClick={prevPhoto} style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,.45)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>‹</button>
                                  <button onClick={nextPhoto} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 26, height: 26, borderRadius: '50%', background: 'rgba(0,0,0,.45)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>›</button>
                                  {/* Dot indicators */}
                                  <div style={{ position: 'absolute', bottom: 7, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5 }}>
                                    {photos.map((_, i) => (
                                      <div key={i} onClick={e => { e.stopPropagation(); setPhotoIdx(p => ({ ...p, [cardKey]: i })); }}
                                        style={{ width: i === cur ? 14 : 6, height: 6, borderRadius: 3, background: i === cur ? '#fff' : 'rgba(255,255,255,.5)', cursor: 'pointer', transition: 'all .15s' }} />
                                    ))}
                                  </div>
                                </>
                              )}

                              {/* Selected badge */}
                              {isSel && (
                                <div style={{ position: 'absolute', top: 8, right: 8, background: 'var(--ac)', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 10, padding: '3px 8px' }}>✓ Selected</div>
                              )}
                              {/* Availability count for model groups */}
                              {isModelGroup && (
                                <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,.65)', color: '#fff', fontSize: 9, fontWeight: 600, borderRadius: 10, padding: '3px 8px' }}>
                                  {availabilityCount} of {totalCount} available
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Content */}
                        <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>

                          {/* Name + group */}
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{v.name}</div>
                            {grp && <div style={{ fontSize: 10, color: 'var(--ac)', marginTop: 2 }}>or similar · {grp.icon} {grp.name}</div>}
                          </div>

                          {/* Feature chips — structured */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, justifyContent: 'center' }}>
                            {(() => {
                              const chip = (icon, label) => (
                                <span style={{ fontSize: 10, background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 20, padding: '2px 8px', color: 'var(--tx2)', whiteSpace: 'nowrap' }}>
                                  {icon} {label}
                                </span>
                              );
                              return (<>
                                {v.seats        && chip('👥', `${v.seats} seat${parseInt(v.seats) !== 1 ? 's' : ''}`)}
                                {v.transmission && chip('⚙️', v.transmission === 'automatic' ? 'Automatic' : 'Manual')}
                                {v.mileage      && chip('♾️', v.mileage === 'unlimited' ? 'Unlimited' : 'Limited km')}
                                {v.fuelType     && chip(v.fuelType === 'electric' ? '⚡' : v.fuelType === 'hybrid' ? '🌿' : '⛽', v.fuelType.charAt(0).toUpperCase() + v.fuelType.slice(1))}
                                {v.features && v.features.split(',')
                                  .map(f => f.trim())
                                  .filter(f => {
                                    const low = f.toLowerCase();
                                    // Skip words already shown as structured chips
                                    if (['auto','automatic','manual','unlimited','limited','petrol','diesel','electric','hybrid'].includes(low)) return false;
                                    return true;
                                  })
                                  .slice(0, 3)
                                  .map((f, i) => (
                                    <span key={i} style={{ fontSize: 10, background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 20, padding: '2px 8px', color: 'var(--tx2)', whiteSpace: 'nowrap' }}>
                                      {f}
                                    </span>
                                  ))}
                              </>);
                            })()}
                          </div>

                          {/* Price */}
                          <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: 6, borderTop: '1px solid var(--bd)' }}>
                            <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 2 }}>
                              Total for {bk.days} day{bk.days !== 1 ? 's' : ''}
                            </div>
                            {hasRules ? (
                              <div>
                                <span style={{ fontSize: 12, color: 'var(--tx3)', textDecoration: 'line-through', marginRight: 6 }}>€{rawTotal.toFixed(0)}</span>
                                <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--gr)', fontFamily: "'DM Mono',monospace" }}>€{discountedTotal.toFixed(0)}</span>
                                <div style={{ fontSize: 9, color: 'var(--gr)', marginTop: 2 }}>
                                  {vRules.map(r => `${r.effect === 'discount' ? '−' : '+'}${r.value}${r.valueType === 'percent' ? '%' : '€'} ${r.showLabel && r.label ? r.label : r.name}`).join(' · ')}
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: 18, fontWeight: 700, fontFamily: "'DM Mono',monospace", color: isSel ? 'var(--ac)' : 'var(--tx)' }}>€{rawTotal.toFixed(0)}</span>
                            )}
                          </div>

                          {/* Button */}
                          <button
                            onClick={e => { e.stopPropagation(); pickVehicle(v.id); }}
                            style={{
                              width: '100%', padding: '9px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                              background: 'var(--ac)',
                              color: '#fff', fontSize: 12, fontWeight: 600, marginTop: 4,
                              transition: 'opacity .12s',
                            }}>
                            {isSel ? '✓ Selected' : 'Select →'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* STEP 3: Extras & Insurance */}
          {bk.step === 3 && (
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Extras & Insurance</div>
              <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 16 }}>Optional add-ons for your rental</div>

              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>➕ Extras</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {extras.filter(e => e.enabled !== false).map(e => {
                  const qty = bk.selectedExtras[e.id] || 0;
                  const sel = qty > 0;
                  const unitPrice = e.perDay ? e.price * bk.days : e.price;
                  const totalPrice = unitPrice * (e.allowQty ? qty : 1);
                  return (
                    <div key={e.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                      border: `2px solid ${sel ? 'var(--gr)' : 'var(--bd)'}`,
                      borderRadius: 'var(--r)',
                      background: sel ? 'var(--gr-l)' : 'var(--sur)', transition: 'all .12s',
                    }}>
                      <div style={{ fontSize: 22, flexShrink: 0 }}>{e.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>{e.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--tx3)' }}>
                          €{e.price}{e.perDay ? '/day' : ' flat'}
                          {sel && <span style={{ color: 'var(--gr)', fontWeight: 600 }}> → €{totalPrice} total</span>}
                        </div>
                      </div>
                      {e.allowQty ? (
                        /* Quantity picker */
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1.5px solid var(--bd)', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                          <button onClick={() => setExtraQty(e.id, qty - 1, e.maxQty)} style={{
                            width: 30, height: 32, background: qty > 0 ? 'var(--sur2)' : 'var(--sur)',
                            border: 'none', fontSize: 16, cursor: qty > 0 ? 'pointer' : 'default',
                            color: qty > 0 ? 'var(--tx)' : 'var(--tx3)',
                          }}>−</button>
                          <div style={{ width: 30, textAlign: 'center', fontSize: 13, fontWeight: 600, background: 'var(--sur)' }}>{qty}</div>
                          <button onClick={() => setExtraQty(e.id, qty + 1, e.maxQty)} style={{
                            width: 30, height: 32, background: qty < (e.maxQty || 10) ? 'var(--sur2)' : 'var(--sur)',
                            border: 'none', fontSize: 16, cursor: qty < (e.maxQty || 10) ? 'pointer' : 'default',
                            color: qty < (e.maxQty || 10) ? 'var(--tx)' : 'var(--tx3)',
                          }}>+</button>
                        </div>
                      ) : (
                        /* Checkbox toggle */
                        <div onClick={() => toggleExtra(e.id)} style={{
                          width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                          border: `2px solid ${sel ? 'var(--gr)' : 'var(--bd)'}`,
                          background: sel ? 'var(--gr)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 12, flexShrink: 0,
                        }}>{sel ? '✓' : ''}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                🛡️ Insurance Coverage <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400 }}>(optional)</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 10 }}>Select a coverage level or choose at pickup.</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
                {vehicleInsPlans.map(ins => {
                  const sel = bk.selectedInsurance === ins.id;
                  const totalIns = (ins.price || 0) * bk.days;
                  return (
                    <div key={ins.id} onClick={() => selectInsurance(ins.id)} style={{
                      border: `2px solid ${sel ? ins.color : 'var(--bd)'}`,
                      borderRadius: 'var(--rl)', padding: '12px 10px', cursor: 'pointer',
                      background: sel ? 'rgba(0,0,0,.03)' : 'var(--sur)', textAlign: 'center', transition: 'all .12s',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: ins.color, marginBottom: 4 }}>{ins.name}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: ins.color, fontFamily: "'DM Mono',monospace" }}>
                        {ins.price > 0 ? <>€{ins.price}<span style={{ fontSize: 9, fontWeight: 400 }}>/day</span></> : <span style={{ fontSize: 13 }}>Included</span>}
                      </div>
                      {ins.price > 0 && <div style={{ fontSize: 10, color: 'var(--tx3)', margin: '2px 0' }}>€{totalIns} total</div>}
                      <div style={{ fontSize: 9, color: 'var(--tx3)', lineHeight: 1.4, margin: '4px 0' }}>{ins.desc}</div>
                      <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 4 }}>Excess: <strong>{ins.excess}</strong></div>
                      <div style={{ marginBottom: 6 }}>
                        {ins.deposit > 0
                          ? <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              background: '#fef2f2', border: '1px solid #fca5a5',
                              borderRadius: 5, padding: '2px 7px',
                              fontSize: 10, color: '#b91c1c', fontWeight: 700,
                            }}>🔒 Deposit: €{ins.deposit}</span>
                          : <span style={{ fontSize: 9, color: 'var(--gr)', fontWeight: 600 }}>🔒 No deposit</span>
                        }
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: sel ? ins.color : 'var(--tx3)' }}>
                        {sel ? '✓ Selected' : 'Tap to select'}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Order Summary Card */}
              {(() => {
                const v = vehicles.find(x => x.id === bk.vid);
                const fv = vatMode === 'inclusive' ? 1 / (1 + vatRateVal / 100) : 1;
                const baseExV = ct.base * fv;
                const baseU = bk.days > 0 ? baseExV / bk.days : 0;
                return (
                  <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,.04)' }}>
                    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--bd)', background: 'var(--sur2)', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 40, height: 32, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--sur)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {v?.photos?.length > 0
                          ? <img src={v.photos[0]} alt={bk.vehName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: 20 }}>{bk.vehIcon}</span>
                        }
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{bk.vehName}</div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{bk.days} days · {bk.d1} → {bk.d2}</div>
                      </div>
                    </div>
                    <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <span style={{ color: 'var(--tx3)' }}>Vehicle rental <span style={{ fontSize: 10 }}>{bk.days}d × €{baseU.toFixed(2)}</span></span>
                        <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{baseExV.toFixed(2)}</span>
                      </div>
                      {ct.insAmt > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: 'var(--tx3)' }}>Insurance — {selIns?.name}</span>
                          <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(ct.insAmt * fv).toFixed(2)}</span>
                        </div>
                      )}
                      {selExtras.map(e => {
                        const qty = bk.selectedExtras[e.id] || 1;
                        const lineT = (e.perDay ? e.price * bk.days : e.price) * qty * fv;
                        return (
                          <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ color: 'var(--tx3)' }}>{e.name}{qty > 1 ? ` ×${qty}` : ''}{e.perDay ? ` (${bk.days}d)` : ''}</span>
                            <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{lineT.toFixed(2)}</span>
                          </div>
                        );
                      })}
                      {ct.locFee > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: 'var(--tx3)' }}>Location fee</span>
                          <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(ct.locFee * fv).toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{ borderTop: '1px dashed var(--bd)', margin: '2px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <span style={{ color: 'var(--tx3)' }}>VAT ({vatRateVal}%){vatMode === 'inclusive' ? ' incl.' : ''}</span>
                        <span style={{ fontFamily: "'DM Mono',monospace" }}>{vatMode === 'exclusive' ? '+' : ''}€{vatAmt.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '2px solid var(--bd)', paddingTop: 8, marginTop: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>Total</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--ac)', fontFamily: "'DM Mono',monospace" }}>€{grandTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <button className="btn btn-primary" onClick={() => goStep(4)}
                style={{ width: '100%', marginTop: 14, justifyContent: 'center', fontSize: 13 }}>
                Continue →
              </button>
            </div>
          )}

          {/* STEP 4: Your Details */}
          {bk.step === 4 && (
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Your Details</div>
              <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 16 }}>We need this to confirm your booking</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>First Name *</label>
                  <input type="text" className="fi" value={bk.fname} placeholder="Andreas"
                    onChange={e => update({ fname: e.target.value })} style={{ fontSize: 12 }} />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Last Name *</label>
                  <input type="text" className="fi" value={bk.lname} placeholder="Andreou"
                    onChange={e => update({ lname: e.target.value })} style={{ fontSize: 12 }} />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Email *</label>
                  <input type="email" className="fi" value={bk.email} placeholder="you@email.com"
                    onChange={e => update({ email: e.target.value })} style={{ fontSize: 12 }} />
                </div>
                {/* Country selector */}
                <div ref={countryRef} style={{ position: 'relative' }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Country *</label>
                  <input
                    className="fi"
                    value={countrySearch}
                    placeholder="Search country..."
                    onFocus={() => setCountryOpen(true)}
                    onChange={e => { setCountrySearch(e.target.value); setCountryOpen(true); }}
                    style={{ fontSize: 12 }}
                  />
                  {countryOpen && (() => {
                    const q = countrySearch.replace(/^[^\w]*/, '').toLowerCase();
                    const filtered = countries.filter(c =>
                      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.dial.includes(q)
                    );
                    return (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 200, overflowY: 'auto',
                        background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: '0 0 8px 8px',
                        boxShadow: '0 6px 20px rgba(0,0,0,.12)', zIndex: 50,
                      }}>
                        {filtered.length === 0 && (
                          <div style={{ padding: '10px 12px', fontSize: 11, color: 'var(--tx3)' }}>No countries found</div>
                        )}
                        {filtered.map(c => (
                          <div
                            key={c.code}
                            onClick={() => selectCountry(c)}
                            style={{
                              padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                              display: 'flex', alignItems: 'center', gap: 8,
                              borderBottom: '1px solid var(--bd)',
                              background: bk.country === c.code ? 'var(--ac-l, rgba(0,100,200,.06))' : 'transparent',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--sur2)'}
                            onMouseLeave={e => e.currentTarget.style.background = bk.country === c.code ? 'var(--ac-l, rgba(0,100,200,.06))' : 'transparent'}
                          >
                            <span style={{ fontSize: 16 }}>{c.flag}</span>
                            <span style={{ flex: 1 }}>{c.name}</span>
                            <span style={{ color: 'var(--tx3)', fontSize: 11 }}>{c.dial}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {/* Phone with country prefix */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Phone *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  {bk.country && (() => {
                    const c = countries.find(x => x.code === bk.country);
                    return c ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '8px 10px',
                        background: 'var(--sur2)', border: '1px solid var(--bd)', borderRight: 'none',
                        borderRadius: '6px 0 0 6px', fontSize: 12, fontWeight: 600,
                        color: 'var(--tx2)', whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                        <span style={{ fontSize: 15 }}>{c.flag}</span>
                        <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11 }}>{c.dial}</span>
                      </div>
                    ) : null;
                  })()}
                  <input type="tel" className="fi" value={bk.phone} placeholder={bk.country ? '99 123 456' : '+357 99 123 456'}
                    onChange={e => update({ phone: e.target.value })}
                    style={{ fontSize: 12, borderRadius: bk.country ? '0 6px 6px 0' : '6px', flex: 1 }} />
                </div>
              </div>

              {/* Identity Documents — filtered by customer requirements */}
              {(() => {
                const idFields = [
                  reqVisible('reqPassportNum') && { label: `Passport / ID Number${reqRequired('reqPassportNum') ? ' (required)' : ' (optional)'}`, key: 'passport', placeholder: 'P1234567', mono: true },
                  reqVisible('reqLicenceNum') && { label: `Driving Licence Number${reqRequired('reqLicenceNum') ? ' (required)' : ' (optional)'}`, key: 'licence', placeholder: 'CY-DL-00000', mono: true },
                  reqVisible('reqLicExpiry') && { label: `Licence Expiry Date${reqRequired('reqLicExpiry') ? ' (required)' : ' (optional)'}`, key: 'licExpiry', type: 'date' },
                ].filter(Boolean);
                if (idFields.length === 0) return null;
                return (
                  <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', padding: 14, marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>🪪 Identity Documents</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 12 }}>Verified at pickup</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {idFields.map(f => (
                        <div key={f.key}>
                          <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>{f.label}</label>
                          <input type={f.type || 'text'} className="fi" value={bk[f.key]} placeholder={f.placeholder || ''}
                            onChange={e => update({ [f.key]: e.target.value })}
                            style={{ fontSize: 12, ...(f.mono ? { fontFamily: "'DM Mono',monospace" } : {}) }} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Document uploads — filtered by customer requirements */}
              {(() => {
                const docFields = [
                  reqVisible('reqLicencePhoto') && { key: 'licenceFile', previewKey: 'licencePreview', label: `Driver's Licence${reqRequired('reqLicencePhoto') ? ' (required)' : ' (optional)'}`, reqKey: 'reqLicencePhoto' },
                  reqVisible('reqPassportPhoto') && { key: 'idFile', previewKey: 'idPreview', label: `Passport / ID${reqRequired('reqPassportPhoto') ? ' (required)' : ' (optional)'}`, reqKey: 'reqPassportPhoto' },
                ].filter(Boolean);
                if (docFields.length === 0) return null;
                return (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>📎 Document Uploads</div>
                <div style={{ display: 'grid', gridTemplateColumns: docFields.length > 1 ? '1fr 1fr' : '1fr', gap: 12 }}>
                  {docFields.map(f => {
                    const file    = bk[f.key];
                    const preview = bk[f.previewKey];
                    function handleFile(e) {
                      const selected = e.target.files[0];
                      if (!selected) return;
                      if (selected.type.startsWith('image/')) {
                        const reader = new FileReader();
                        reader.onload = ev => update({ [f.key]: selected, [f.previewKey]: ev.target.result });
                        reader.readAsDataURL(selected);
                      } else {
                        update({ [f.key]: selected, [f.previewKey]: null });
                      }
                      e.target.value = '';
                    }
                    return (
                      <div key={f.key} style={{ border: `1px solid ${file ? 'var(--gr)' : 'var(--bd)'}`, borderRadius: 'var(--rl)', overflow: 'hidden', background: file ? 'var(--gr-l)' : 'var(--sur2)', transition: 'all .15s' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: file ? 'var(--gr)' : 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '8px 10px 4px' }}>
                          {file ? '✅ ' : ''}{f.label}
                        </div>

                        {/* Preview thumbnail */}
                        {preview && (
                          <div style={{ padding: '0 10px 6px' }}>
                            <img src={preview} alt="doc preview" style={{ width: '100%', maxHeight: 100, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--bd)' }} />
                          </div>
                        )}
                        {file && !preview && (
                          <div style={{ padding: '0 10px 6px', fontSize: 11, color: 'var(--tx2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            📄 {file.name}
                          </div>
                        )}

                        {/* Buttons row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid var(--bd)' }}>
                          {/* Upload */}
                          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '8px 6px', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--tx2)', borderRight: '1px solid var(--bd)', background: 'transparent' }}>
                            <span>📁</span> Upload
                            <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleFile} />
                          </label>
                          {/* Camera */}
                          <button
                            onClick={() => setCamera({ key: f.key, previewKey: f.previewKey, label: f.label })}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '8px 6px', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--tx2)', background: 'transparent', border: 'none', width: '100%' }}>
                            <span>📷</span> Camera
                          </button>
                        </div>

                        {/* Clear */}
                        {file && (
                          <button onClick={() => update({ [f.key]: null, [f.previewKey]: null })}
                            style={{ width: '100%', padding: '5px', fontSize: 10, color: 'var(--ac)', background: 'none', border: 'none', borderTop: '1px solid var(--bd)', cursor: 'pointer' }}>
                            ✕ Remove
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
                );
              })()}

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Special Requests / Notes</label>
                <textarea className="fi" rows={2} value={bk.notes} placeholder="Baby seat, special pickup instructions..."
                  onChange={e => update({ notes: e.target.value })} style={{ fontSize: 12 }} />
              </div>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, color: 'var(--tx2)', cursor: 'pointer', marginBottom: 14 }}>
                <input type="checkbox" checked={bk.termsAgreed} onChange={e => update({ termsAgreed: e.target.checked })}
                  style={{ marginTop: 2, accentColor: 'var(--ac)', flexShrink: 0 }} />
                I agree to the <span style={{ color: 'var(--ac)' }}>Terms & Conditions</span> and <span style={{ color: 'var(--ac)' }}>Privacy Policy</span>
              </label>

              {/* Rental Agreement + Pre-sign — conditional on reqSignAgreement */}
              {agreementVisible && (() => {
                const v = vehicles.find(x => x.id === bk.vid);
                const deposit = selIns?.deposit ?? v?.deposit ?? 200;
                const agreementLabel = reqRequired('reqSignAgreement') ? '(required)' : '(optional)';
                return (
                  <div style={{ marginBottom: 14, background: 'var(--sur2)', border: `1px solid ${bk.presigned ? 'var(--gr)' : 'var(--bd)'}`, borderRadius: 'var(--rl)', overflow: 'hidden', transition: 'border-color .15s' }}>
                    <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                      onClick={() => update({ presignOpen: !bk.presignOpen })}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>✍️ Pre-sign Rental Agreement <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400 }}>{agreementLabel}</span></div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>
                          {bk.presigned ? '✅ Signed — no paperwork needed at pickup!' : 'Read and sign the agreement before payment'}
                        </div>
                      </div>
                      <div style={{ fontSize: 14, transform: bk.presignOpen ? 'rotate(180deg)' : '', transition: 'transform .2s', color: 'var(--tx3)' }}>▼</div>
                    </div>

                    {bk.presignOpen && (
                      <div style={{ padding: 14, borderTop: '1px solid var(--bd)' }}>
                        {/* Contract document — same renderer used everywhere else */}
                        <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--bd)', borderRadius: 8, marginBottom: 14, background: 'var(--sur2)', padding: 10 }}>
                          {(() => {
                            const tmpl = getAgreementForVehicle(bk.vid);
                            const v2 = vehicles.find(x => x.id === bk.vid);
                            const ctx = buildContractContext({
                              rental: {
                                id: 'PREVIEW',
                                customer: `${bk.fname || ''} ${bk.lname || ''}`.trim(),
                                passport: bk.passport,
                                licence: bk.licence,
                                licExpiry: bk.licExpiry,
                                email: bk.email,
                                phone: (bk.country ? (countries.find(x => x.code === bk.country)?.dial + ' ') : '') + (bk.phone || ''),
                                nationality: bk.countryName,
                                startLabel: bk.d1, endLabel: bk.d2,
                                t1: bk.t1, t2: bk.t2,
                                pickupLocName: pickLoc?.name,
                                returnLocName: bk.diffReturn ? retLoc?.name : pickLoc?.name,
                                calSpan: bk.days,
                                rate: bk.rate,
                                deposit, depositMethod: bk.payMethod,
                                agreedPrice: grandTotal,
                                insurance: selIns?.name,
                                insuranceExcess: selIns?.excess,
                                extras: selExtras.map(e => e.name),
                              },
                              vehicle: v2,
                              company: companySettings,
                              signatureDataUrl: bk.presignData || '',
                              signedAt: bk.presigned ? new Date().toISOString() : '',
                            });
                            return tmpl
                              ? <ContractDocument template={tmpl} context={ctx} paperLook={true} />
                              : <div style={{ padding: 20, color: 'var(--tx3)', fontSize: 11 }}>No agreement template configured for this vehicle.</div>;
                          })()}
                        </div>

                        {/* Signature canvas */}
                        {bk.presigned ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, color: 'var(--gr)', fontWeight: 600 }}>✅ Agreement pre-signed — {bk.presignedAt}</div>
                              {bk.presignData && <img src={bk.presignData} alt="signature" style={{ marginTop: 6, maxHeight: 50, background: '#fff', border: '1px solid var(--bd)', borderRadius: 4, objectFit: 'contain', width: 180 }} />}
                            </div>
                            <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); update({ presigned: false, presignData: null, presignedAt: null }); clearCanvas(); }}>Clear</button>
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 6 }}>Draw your signature below to sign:</div>
                            <canvas ref={canvasRef} width={500} height={100}
                              style={{ display: 'block', width: '100%', background: '#fff', border: '1px solid var(--bd)', borderRadius: 7, cursor: 'crosshair', touchAction: 'none', marginBottom: 8 }}
                              onMouseDown={canvasStart} onMouseMove={canvasMove} onMouseUp={canvasStop} onMouseLeave={canvasStop}
                              onTouchStart={canvasStart} onTouchMove={canvasMove} onTouchEnd={canvasStop} />
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); clearCanvas(); }}>Clear</button>
                              <button className="btn btn-primary btn-sm" style={{ background: 'var(--gr)', borderColor: 'var(--gr)' }} onClick={e => { e.stopPropagation(); saveSignature(); }}>✓ Sign Agreement</button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Order Summary Card */}
              {(() => {
                const v = vehicles.find(x => x.id === bk.vid);
                const fv = vatMode === 'inclusive' ? 1 / (1 + vatRateVal / 100) : 1;
                const baseExV = ct.base * fv;
                const baseU = bk.days > 0 ? baseExV / bk.days : 0;
                return (
                  <div style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 14, overflow: 'hidden', marginBottom: 14, boxShadow: '0 2px 8px rgba(0,0,0,.04)' }}>
                    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--bd)', background: 'var(--sur2)', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 40, height: 32, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--sur)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {v?.photos?.length > 0
                          ? <img src={v.photos[0]} alt={bk.vehName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: 20 }}>{bk.vehIcon}</span>
                        }
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{bk.vehName}</div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{bk.days} days · {bk.d1} → {bk.d2}</div>
                      </div>
                    </div>
                    <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <span style={{ color: 'var(--tx3)' }}>Vehicle rental <span style={{ fontSize: 10 }}>{bk.days}d × €{baseU.toFixed(2)}</span></span>
                        <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{baseExV.toFixed(2)}</span>
                      </div>
                      {ct.insAmt > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: 'var(--tx3)' }}>Insurance — {selIns?.name}</span>
                          <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(ct.insAmt * fv).toFixed(2)}</span>
                        </div>
                      )}
                      {selExtras.map(e => {
                        const qty = bk.selectedExtras[e.id] || 1;
                        const lineT = (e.perDay ? e.price * bk.days : e.price) * qty * fv;
                        return (
                          <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ color: 'var(--tx3)' }}>{e.name}{qty > 1 ? ` ×${qty}` : ''}{e.perDay ? ` (${bk.days}d)` : ''}</span>
                            <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{lineT.toFixed(2)}</span>
                          </div>
                        );
                      })}
                      {ct.locFee > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: 'var(--tx3)' }}>Location fee</span>
                          <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(ct.locFee * fv).toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{ borderTop: '1px dashed var(--bd)', margin: '2px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <span style={{ color: 'var(--tx3)' }}>VAT ({vatRateVal}%){vatMode === 'inclusive' ? ' incl.' : ''}</span>
                        <span style={{ fontFamily: "'DM Mono',monospace" }}>{vatMode === 'exclusive' ? '+' : ''}€{vatAmt.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '2px solid var(--bd)', paddingTop: 8, marginTop: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 700 }}>Total</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--ac)', fontFamily: "'DM Mono',monospace" }}>€{grandTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <button className="btn btn-primary" onClick={() => goStep(5)}
                style={{ width: '100%', justifyContent: 'center', fontSize: 13 }}>
                Continue to Payment →
              </button>
            </div>
          )}

          {/* STEP 5: Review & Payment */}
          {bk.step === 5 && (() => {
            const v = vehicles.find(x => x.id === bk.vid);
            const deposit = selIns?.deposit ?? v?.deposit ?? 200;
            const f = vatMode === 'inclusive' ? 1 / (1 + vatRateVal / 100) : 1;
            const baseExVat = ct.base * f;
            const baseUnit  = bk.days > 0 ? baseExVat / bk.days : 0;
            const fmtDate = (iso) => {
              const d = new Date(iso);
              return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
            };
            return (
              <div style={{ padding: '24px 20px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>Order Summary</div>
                <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 20 }}>Review your booking details before confirming</div>

                {/* ── Vehicle Hero Card ── */}
                <div style={{
                  background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 14,
                  overflow: 'hidden', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,.04)',
                }}>
                  {/* Vehicle header with photo */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderBottom: '1px solid var(--bd)' }}>
                    <div style={{
                      width: 64, height: 48, borderRadius: 10, overflow: 'hidden', flexShrink: 0,
                      background: 'var(--sur2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {v?.photos?.length > 0
                        ? <img src={v.photos[0]} alt={bk.vehName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 28 }}>{bk.vehIcon}</span>
                      }
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{bk.vehName}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>
                        {v?.transmission ? v.transmission.charAt(0).toUpperCase() + v.transmission.slice(1) : ''}{v?.fuelType ? ` · ${v.fuelType.charAt(0).toUpperCase() + v.fuelType.slice(1)}` : ''}{v?.seats ? ` · ${v.seats}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ac)' }}>€{ct.effectiveRate}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>per day</div>
                    </div>
                  </div>

                  {/* Pickup / Return rows */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid var(--bd)' }}>
                    <div style={{ padding: '12px 16px', borderRight: '1px solid var(--bd)' }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>Pick-up</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{fmtDate(bk.d1)}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 2 }}>{bk.t1} · {pickLoc?.name || '—'}</div>
                    </div>
                    <div style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>Return</div>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{fmtDate(bk.d2)}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 2 }}>{bk.t2} · {bk.diffReturn ? retLoc?.name : pickLoc?.name || '—'}</div>
                    </div>
                  </div>

                  {/* Duration badge */}
                  <div style={{ padding: '10px 16px', background: 'var(--sur2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--tx3)" strokeWidth="2" style={{ width: 14, height: 14 }}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{bk.days} {bk.days === 1 ? 'day' : 'days'} rental</span>
                  </div>
                </div>

                {/* ── Price Breakdown Card ── */}
                <div style={{
                  background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 14,
                  overflow: 'hidden', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,.04)',
                }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--bd)', background: 'var(--sur2)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>Price Breakdown</div>
                  </div>
                  <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Vehicle base */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                      <div>
                        <span style={{ fontWeight: 500 }}>Vehicle rental</span>
                        <span style={{ color: 'var(--tx3)', marginLeft: 6 }}>{bk.days}d × €{baseUnit.toFixed(2)}</span>
                      </div>
                      <span style={{ fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>€{baseExVat.toFixed(2)}</span>
                    </div>
                    {/* Rate rule adjustments */}
                    {ct.applicableRules.map(r => {
                      const amt = (r.valueType === 'percent' ? ct.rawBase * r.value / 100 : r.value * bk.days) * f;
                      return (
                        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: r.effect === 'discount' ? 'var(--gr)' : '#ef4444' }}>
                          <span style={{ fontWeight: 600 }}>{r.showLabel && r.label ? r.label : r.name}</span>
                          <span style={{ fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>{r.effect === 'discount' ? '−' : '+'}€{amt.toFixed(2)}</span>
                        </div>
                      );
                    })}
                    {/* Insurance */}
                    {ct.insAmt > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                        <div>
                          <span style={{ fontWeight: 500 }}>Insurance</span>
                          <span style={{ color: 'var(--tx3)', marginLeft: 6 }}>{selIns?.name}</span>
                        </div>
                        <span style={{ fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>€{(ct.insAmt * f).toFixed(2)}</span>
                      </div>
                    )}
                    {/* Extras */}
                    {selExtras.map(e => {
                      const qty = bk.selectedExtras[e.id] || 1;
                      const lineTotal = (e.perDay ? e.price * bk.days : e.price) * qty * f;
                      const unitEx = e.price * f;
                      const detail = e.perDay
                        ? `${qty > 1 ? `${qty} × ` : ''}${bk.days}d × €${unitEx.toFixed(2)}`
                        : qty > 1 ? `×${qty}` : '';
                      return (
                        <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                          <div>
                            <span style={{ fontWeight: 500 }}>{e.name}</span>
                            {detail && <span style={{ color: 'var(--tx3)', marginLeft: 6 }}>{detail}</span>}
                          </div>
                          <span style={{ fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>€{lineTotal.toFixed(2)}</span>
                        </div>
                      );
                    })}
                    {/* Location fee */}
                    {ct.locFee > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                        <span style={{ fontWeight: 500 }}>Delivery / Location fee</span>
                        <span style={{ fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>€{(ct.locFee * f).toFixed(2)}</span>
                      </div>
                    )}
                    {/* Promo discount */}
                    {ct.promoDiscount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--gr)' }}>
                        <span style={{ fontWeight: 600 }}>Promo code ({bk.promoCode})</span>
                        <span style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>−€{(ct.promoDiscount * f).toFixed(2)}</span>
                      </div>
                    )}

                    {/* Divider */}
                    <div style={{ borderTop: '1px dashed var(--bd)', margin: '4px 0' }} />

                    {/* Subtotal */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                      <span style={{ color: 'var(--tx3)' }}>Subtotal</span>
                      <span style={{ fontFamily: "'DM Mono',monospace" }}>€{(ct.total * f).toFixed(2)}</span>
                    </div>
                    {/* VAT */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                      <span style={{ color: 'var(--tx3)' }}>VAT ({vatRateVal}%){vatMode === 'inclusive' ? ' incl.' : ''}</span>
                      <span style={{ fontFamily: "'DM Mono',monospace" }}>{vatMode === 'exclusive' ? '+' : ''}€{vatAmt.toFixed(2)}</span>
                    </div>

                    {/* Grand Total */}
                    <div style={{
                      borderTop: '2px solid var(--bd)', paddingTop: 10, marginTop: 4,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>Total</span>
                      <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--ac)', fontFamily: "'DM Mono',monospace" }}>€{grandTotal.toFixed(2)}</span>
                    </div>
                    {deposit > 0 && (
                      <div style={{ fontSize: 10, color: 'var(--tx3)', textAlign: 'right', marginTop: -2 }}>
                        + €{deposit} refundable deposit at pickup
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Promo Code ── */}
                {(promoCodes || []).length > 0 && (
                  <div style={{
                    background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 14,
                    padding: '12px 16px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,.04)',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Have a promo code?</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="text" className="fi" placeholder="Enter code..." value={bk.promoCode || ''}
                        onChange={e => update({ promoCode: e.target.value.toUpperCase() })}
                        style={{ fontFamily: "'DM Mono',monospace", letterSpacing: 1, flex: 1, fontSize: 12, textTransform: 'uppercase' }} />
                      {bk.promoCode && (() => {
                        const pc = (promoCodes || []).find(p => p.code === bk.promoCode.toUpperCase() && p.active);
                        const expired = pc?.expiryDate && new Date(pc.expiryDate) < new Date();
                        const exhausted = pc?.usageLimit && pc.usedCount >= pc.usageLimit;
                        const valid = pc && !expired && !exhausted;
                        return (
                          <span style={{
                            fontSize: 11, fontWeight: 700, flexShrink: 0, padding: '4px 10px', borderRadius: 6,
                            color: valid ? 'var(--gr)' : '#ef4444',
                            background: valid ? 'var(--gr-l)' : '#fef2f2',
                          }}>{valid ? '✓ Applied' : '✗ Invalid'}</span>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* ── Payment Method ── */}
                <div style={{
                  background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 14,
                  overflow: 'hidden', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,.04)',
                }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--bd)', background: 'var(--sur2)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>Payment Method</div>
                  </div>
                  <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { method: 'stripe', icon: '💳', title: 'Pay by Card Online', desc: `Pay €${grandTotal.toFixed(2)} now`, color: 'var(--bl)', bg: 'var(--bl-l)', bd: 'var(--bl-b)' },
                      { method: 'bank', icon: '🏦', title: 'Bank Transfer', desc: `Pro forma + payment details (IBAN) sent by email`, color: 'var(--am)', bg: 'var(--am-l)', bd: 'var(--am-b)' },
                      { method: 'cash', icon: '💵', title: 'Pay Cash at Pickup', desc: `No charge today`, color: 'var(--gr)', bg: 'var(--gr-l)', bd: 'var(--gr-b)' },
                    ].map(opt => {
                      const sel = bk.payMethod === opt.method;
                      return (
                        <div key={opt.method} onClick={() => selectPayOpt(opt.method)} style={{
                          border: `1.5px solid ${sel ? opt.color : 'var(--bd)'}`,
                          borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
                          background: sel ? opt.bg : 'transparent', transition: 'all .15s',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ fontSize: 20 }}>{opt.icon}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{opt.title}</div>
                              <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{opt.desc}</div>
                            </div>
                            <div style={{
                              width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                              border: `2px solid ${sel ? opt.color : 'var(--bd)'}`,
                              background: sel ? opt.color : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all .15s',
                            }}>
                              {sel && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                            </div>
                          </div>
                          {sel && opt.method === 'bank' && (
                            <div style={{ marginTop: 7, background: opt.bg, border: `1px solid ${opt.bd}`, borderRadius: 6, padding: '7px 10px', fontSize: 10, color: opt.color }}>
                              Booking held until payment confirmed. We'll email you the IBAN details.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ── Confirm Button ── */}
                <button className="btn btn-primary" onClick={submit}
                  style={{
                    width: '100%', justifyContent: 'center', fontSize: 14, fontWeight: 700,
                    padding: '14px 20px', borderRadius: 12, gap: 8,
                  }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 16, height: 16 }}><path d="M5 13l4 4L19 7"/></svg>
                  Confirm Booking · €{grandTotal.toFixed(2)}
                </button>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 10 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--tx3)" strokeWidth="2" style={{ width: 12, height: 12 }}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                  <span style={{ fontSize: 10, color: 'var(--tx3)' }}>Secure booking · Your data is protected</span>
                </div>
              </div>
            );
          })()}

          {/* STEP 6: Confirmed */}
          {bk.step === 6 && (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>Booking Confirmed!</div>
              <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 16 }}>Your booking reference is:</div>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "'DM Mono',monospace", color: 'var(--ac)', background: 'var(--ac-l)', border: '1px solid var(--ac-b)', padding: '10px 20px', borderRadius: 'var(--r)', display: 'inline-block', marginBottom: 16 }}>
                {confirmRef}
              </div>
              <div style={{ fontSize: 12, color: 'var(--tx2)', marginBottom: 12 }}>
                A confirmation has been sent to <strong>{bk.email}</strong>
              </div>

              {/* Details */}
              <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)', padding: 12, fontSize: 11, color: 'var(--tx2)', marginBottom: 14, textAlign: 'left' }}>
                {bk.payMethod === 'cash' && <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: 'var(--gr)', marginBottom: 10 }}>💵 <strong>Reservation confirmed</strong> — Pay €{grandTotal.toFixed(2)} + deposit at pickup. No charge today.</div>}
                {bk.payMethod === 'bank' && <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: 'var(--am)', marginBottom: 10 }}>🏦 <strong>Bank transfer pending</strong> — Pro forma + payment details (IBAN) sent to {bk.email}.</div>}
                {bk.payMethod === 'stripe' && <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: 'var(--bl)', marginBottom: 10 }}>💳 <strong>Payment confirmed</strong> — €{grandTotal.toFixed(2)} charged.</div>}
                <strong>{bk.vehIcon} {bk.vehName}</strong><br />
                📅 {bk.d1} {bk.t1} → {bk.d2} {bk.t2}<br />
                📍 Pickup: {pickLoc?.name}{bk.diffReturn && retLoc?.id !== pickLoc?.id ? ' · Return: ' + retLoc?.name : ''}<br />
                {selExtras.length > 0 && <>➕ Extras: {selExtras.map(e => e.name).join(', ')}<br /></>}
                💰 Total: €{grandTotal.toFixed(2)}{vatMode === 'inclusive' ? ' (VAT incl.)' : ` (incl. €${vatAmt.toFixed(2)} VAT)`}
              </div>

              <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)', padding: 14, marginBottom: 14, textAlign: 'left' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>🔗 Manage your booking</div>
                <div style={{ fontSize: 11, color: 'var(--tx2)', marginBottom: 8 }}>Use this reference to manage or cancel your booking:</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '8px 10px', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--bl)', fontFamily: "'DM Mono',monospace", flex: 1 }}>fleetrent.app/manage/{confirmRef}</div>
                  <button className="btn btn-ghost btn-sm" onClick={() => showToast('Link copied!')}>Copy</button>
                </div>
                <button className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => { close(); openModal('manage-booking', confirmRentalId); }}>
                  📋 Open Manage Booking Page
                </button>
              </div>

              <div style={{ fontSize: 11, color: 'var(--tx3)', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                Please bring your <strong>passport/ID</strong> and <strong>driving licence</strong> on the day of pickup.
              </div>

              <button className="btn btn-ghost" style={{ marginTop: 14 }} onClick={() => { close(); }}>Close</button>
            </div>
          )}

        </div>

        {/* Bottom nav */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--bd)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: 'var(--sur)' }}>
          {bk.step > 1 && bk.step < 6 ? (
            <button className="btn btn-ghost btn-sm" onClick={() => goStep(bk.step - 1)}>← Back</button>
          ) : <div />}
          <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Powered by <strong style={{ color: 'var(--ac)' }}>FleetRent CRM</strong></div>
          <div style={{ fontSize: 11, color: 'var(--tx2)' }}>
            {grandTotal > 0 ? `Total: €${grandTotal.toFixed(2)}${vatMode === 'inclusive' ? ' (VAT incl.)' : ''}` : ''}
          </div>
        </div>

      </div>
    </div>

    {/* Camera overlay */}
    {camera && (
      <CameraCapture
        label={camera.label}
        onCapture={dataUrl => {
          update({ [camera.key]: { name: camera.label + '.jpg' }, [camera.previewKey]: dataUrl });
          setCamera(null);
        }}
        onClose={() => setCamera(null)}
      />
    )}
  </>
  );
}
